import type { DurationStyle, DurationUnit, FormTypeEnum } from "@/services/entity.service";

const DEFAULT_LOCALE = "de-DE";
const DEFAULT_CURRENCY = "EUR";
const ISO_4217 = /^[A-Z]{3}$/;

export interface FormatNumberOptions {
  fieldType?: string;
  formType?: FormTypeEnum | null;
  decimals?: number | null;
  grouping?: boolean | null;
  currency?: string | null;
  locale?: string | null;
  durationUnit?: DurationUnit | null;
  durationStyle?: DurationStyle | null;
}

export const SECONDS_PER_UNIT: Record<DurationUnit, number> = {
  SECONDS: 1,
  MINUTES: 60,
  HOURS: 3600,
};

// Intl.NumberFormat construction is expensive (locale lookup + format compilation);
// the table renders thousands of cells, so cache instances by their config.
const formatterCache = new Map<string, Intl.NumberFormat>();
const getFormatter = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let instance = formatterCache.get(key);
  if (!instance) {
    instance = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, instance);
  }
  return instance;
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
};

export function formatFieldValue(value: unknown, opts: FormatNumberOptions): string {
  if (value === null || value === undefined || value === "") return "";

  const fieldType = opts.fieldType?.toUpperCase();
  const formType = opts.formType ?? null;
  const locale = opts.locale || DEFAULT_LOCALE;
  const grouping = opts.grouping ?? true;
  const hasExplicitDecimals = opts.decimals != null;
  const minFraction = hasExplicitDecimals ? opts.decimals! : 0;
  const maxFraction = hasExplicitDecimals ? opts.decimals! : 20;

  if (formType === "CURRENCY") {
    const numericValue = toNumber(value);
    if (numericValue === null) return "";
    const rawCurrency = (opts.currency || DEFAULT_CURRENCY).toUpperCase();
    const currency = ISO_4217.test(rawCurrency) ? rawCurrency : DEFAULT_CURRENCY;
    try {
      return getFormatter(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: minFraction,
        maximumFractionDigits: maxFraction,
        useGrouping: grouping,
      }).format(numericValue);
    } catch {
      return getFormatter(locale, {
        minimumFractionDigits: minFraction,
        maximumFractionDigits: maxFraction,
        useGrouping: grouping,
      }).format(numericValue);
    }
  }

  if (formType === "PERCENT") {
    const numericValue = toNumber(value);
    if (numericValue === null) return "";
    return getFormatter(locale, {
      style: "percent",
      minimumFractionDigits: minFraction,
      maximumFractionDigits: maxFraction,
      useGrouping: grouping,
    }).format(numericValue);
  }

  if (formType === "DURATION") {
    const numericValue = toNumber(value);
    if (numericValue === null) return "";
    const seconds = numericValue * SECONDS_PER_UNIT[opts.durationUnit ?? "SECONDS"];
    if (opts.durationStyle === "CLOCK") {
      const totalMinutes = Math.round(Math.abs(seconds) / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = String(totalMinutes % 60).padStart(2, "0");
      const sign = seconds < 0 && totalMinutes > 0 ? "-" : "";
      return `${sign}${getFormatter(locale, { useGrouping: grouping }).format(hours)}:${minutes}`;
    }
    return `${getFormatter(locale, {
      useGrouping: grouping,
      minimumFractionDigits: hasExplicitDecimals ? opts.decimals! : 0,
      maximumFractionDigits: hasExplicitDecimals ? opts.decimals! : 2,
    }).format(seconds / 3600)} h`;
  }

  if (fieldType === "INT" || fieldType === "FLOAT") {
    const numericValue = toNumber(value);
    if (numericValue === null) return "";
    return getFormatter(locale, {
      useGrouping: grouping,
      minimumFractionDigits: minFraction,
      maximumFractionDigits: hasExplicitDecimals ? opts.decimals! : fieldType === "FLOAT" ? 20 : 0,
    }).format(numericValue);
  }

  return String(value);
}

export function getCurrencySymbol(
  currency: string | null | undefined,
  locale?: string | null,
): string {
  const code = (currency || DEFAULT_CURRENCY).toUpperCase();
  if (!ISO_4217.test(code)) return code;
  try {
    const parts = getFormatter(locale || DEFAULT_LOCALE, {
      style: "currency",
      currency: code,
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? code;
  } catch {
    return code;
  }
}

const trimPrecision = (n: number): string => parseFloat(n.toPrecision(12)).toString();

export const percentFractionToDisplay = (stored: string): string => {
  if (stored === "" || stored === "-") return stored;
  const n = parseFloat(stored);
  return Number.isFinite(n) ? trimPrecision(n * 100) : stored;
};

export const percentDisplayToFraction = (display: string): string => {
  if (display === "" || display === "-") return display;
  const n = parseFloat(display);
  return Number.isFinite(n) ? trimPrecision(n / 100) : display;
};
